import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { NavContentNavItem } from '@backstage/plugin-app-react';
import {
  Button,
  Separator,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  cn,
} from '@vivicta/ui';
import {
  ChevronDown,
  FileText,
  Landmark,
  type LucideIcon,
  Menu,
  Network,
  Puzzle,
  ShieldAlert,
  Upload,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import wordmarkUrl from '../../assets/vivicta-wordmark.svg';
import {
  EXPLORE_ENTRIES,
  FOOTER_ENTRIES,
  GOVERNANCE_ENTRIES,
  navIcon as icon,
  WORKSPACE_ENTRIES,
  type NavEntry,
} from './navEntries';
import { PortalTopBar } from './PortalTopBar';
import { useSidebarCollapsed } from './useSidebarCollapsed';

const MAIN_NAVIGATION_ID = 'main-navigation';

const linkClasses = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    isActive
      ? 'bg-primary/15 text-primary'
      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
  );

const NavEntryLink = ({
  entry,
  onNavigate,
}: {
  entry: NavEntry;
  onNavigate?: () => void;
}) => (
  <NavLink
    to={entry.to}
    className={linkClasses}
    onClick={onNavigate}
    end={entry.to === '/'}
  >
    {entry.icon}
    <span className="truncate" title={entry.title}>
      {entry.title}
    </span>
    {entry.badge}
  </NavLink>
);

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
    {children}
  </p>
);

const GovernanceGroup = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { pathname } = useLocation();
  const isActiveRoute = GOVERNANCE_ENTRIES.some(entry =>
    pathname.startsWith(entry.to),
  );
  // Closed by default, but expanded on load if the current route lives inside it.
  const [open, setOpen] = useState(isActiveRoute);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium',
          'text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        {icon(Landmark)}
        <span className="flex-1 text-left">Governance</span>
        <ChevronDown
          aria-hidden="true"
          className={cn('size-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-border pl-2">
          {GOVERNANCE_ENTRIES.map(entry => (
            <NavEntryLink
              key={entry.to}
              entry={entry}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Gives the routed content region a `<main>` landmark and an id for the skip
 * link to target.
 *
 * Backstage owns that element (it is rendered by `SidebarPage`/`Content`, not by
 * us), so there is no prop to set this through — the app previously shipped no
 * main landmark and no way to bypass the 15-item sidebar, failing WCAG 2.4.1
 * (docs/ux-design-review.md §2). Re-stamped per route because the content root
 * is replaced when a page unmounts.
 *
 * Matched on the element rather than a class: both `Content` implementations in
 * play (`@backstage/core-components` and `@vivicta/ui`) render an `<article>`,
 * and the Backstage one is styled with JSS, whose readable
 * `BackstageContent-root` class name only exists in development builds.
 */
const useMainLandmark = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const stamp = () => {
      const content = document.querySelector<HTMLElement>('#root article');
      if (!content || content.id === 'main-content') return;
      content.id = 'main-content';
      content.setAttribute('role', 'main');
      // Focusable only via the skip link, so focus actually lands in the content.
      content.setAttribute('tabindex', '-1');
    };

    stamp();

    /* Routes are code-split, so on a cold navigation the content mounts after
     * this effect. Pages such as the catalog also swap their loading `<article>`
     * for the loaded one, so keep watching for the route's lifetime. */
    const observer = new MutationObserver(stamp);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pathname]);
};

/**
 * Stock plugin nav items bring their own Material icons (APIs' is solid), so
 * the known ones get a line icon like Workspace's and, where it doesn't fit,
 * a shorter title.
 */
const DISCOVERED_OVERRIDES: Record<
  string,
  { icon: LucideIcon; title?: string }
> = {
  '/api-docs': { icon: Puzzle },
  '/catalog-graph': { icon: Network },
  '/catalog-import': { icon: Upload, title: 'Register Component' },
  '/docs': { icon: FileText },
};

/** Catalog views first, then reference pages, then adding to the catalog. */
const EXPLORE_ORDER = [
  '/catalog',
  '/catalog-graph',
  '/repository-map',
  '/api-docs',
  '/docs',
  '/catalog-import',
];

/** Explore's own entries and the discovered ones in EXPLORE_ORDER; unknown pages go last, as found. */
const exploreEntries = (discovered: NavEntry[]) => {
  const rank = (entry: NavEntry) => {
    const index = EXPLORE_ORDER.indexOf(entry.to);
    return index === -1 ? EXPLORE_ORDER.length : index;
  };
  return [...EXPLORE_ENTRIES, ...discovered].sort((a, b) => rank(a) - rank(b));
};

const toEntry = (item: NavContentNavItem): NavEntry => {
  const override = DISCOVERED_OVERRIDES[item.href];
  return {
    to: item.href,
    title: override?.title ?? item.title,
    icon: override ? (
      icon(override.icon)
    ) : (
      <span className="flex size-4 shrink-0 items-center justify-center">
        {item.icon}
      </span>
    ),
  };
};

/**
 * Keeps platform diagnostics out of the way of the product surfaces (T1.4)
 * by collapsing them rather than hiding them. This used to additionally
 * require `group:default/platform-team` ownership, which no session can ever
 * satisfy — `catalog.rules` admits no `User` entities and the group has no
 * members — so the section was unreachable. Re-gate it once the catalog
 * ingests real org membership, alongside T6.1's permission policy.
 */
const AdminGroup = ({
  entries,
  onNavigate,
}: {
  entries: NavEntry[];
  onNavigate?: () => void;
}) => {
  const { pathname } = useLocation();
  const isActiveRoute = entries.some(entry => pathname.startsWith(entry.to));
  const [open, setOpen] = useState(isActiveRoute);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium',
          'text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        {icon(ShieldAlert)}
        <span className="flex-1 text-left">Admin</span>
        <ChevronDown
          aria-hidden="true"
          className={cn('size-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-border pl-2">
          {entries.map(entry => (
            <NavEntryLink
              key={entry.to}
              entry={entry}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Exported for tests only — production code always goes through `SidebarContent`. */
export const SidebarNav = ({
  discovered,
  adminEntries = [],
  onNavigate,
}: {
  discovered: NavEntry[];
  adminEntries?: NavEntry[];
  onNavigate?: () => void;
}) => (
  <div className="flex h-full flex-col gap-1 overflow-y-auto bg-card p-3 text-card-foreground">
    <NavLink
      to="/"
      onClick={onNavigate}
      aria-label="Vivicta Developer Portal home"
      className="mb-1 flex items-center gap-2 rounded-lg px-2 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <img src={wordmarkUrl} alt="Vivicta" className="h-5 w-auto dark:invert" />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        Developer Portal
      </span>
    </NavLink>

    <SectionLabel>Workspace</SectionLabel>
    <div className="flex flex-col gap-0.5">
      {WORKSPACE_ENTRIES.map(entry => (
        <NavEntryLink key={entry.to} entry={entry} onNavigate={onNavigate} />
      ))}
      <GovernanceGroup onNavigate={onNavigate} />
      <AdminGroup entries={adminEntries} onNavigate={onNavigate} />
    </div>

    <SectionLabel>Explore</SectionLabel>
    <div className="flex flex-col gap-0.5">
      {exploreEntries(discovered).map(entry => (
        <NavEntryLink key={entry.to} entry={entry} onNavigate={onNavigate} />
      ))}
    </div>

    <div className="mt-auto flex flex-col gap-0.5 pt-3">
      <Separator className="mb-2" />
      {FOOTER_ENTRIES.map(entry => (
        <NavEntryLink key={entry.to} entry={entry} onNavigate={onNavigate} />
      ))}
    </div>
  </div>
);

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: function VivictaNavContent({ navItems }) {
      const [mobileOpen, setMobileOpen] = useState(false);
      const sidebar = useSidebarCollapsed();
      useMainLandmark();

      /* Every `take()` has to run before the first `rest()`. `navItems` is one
       * memoised, mutable bag shared by every render of this component:
       * `rest()` caches its array on first call and a later `take()` splices
       * that cache — so an item taken after `rest()` shows up under "Explore"
       * on the first render and then vanishes from the sidebar altogether on
       * the next one. */

      // Rendered explicitly above, so drop them from the discovered list
      navItems.take('page:search');
      navItems.take('page:notifications');
      navItems.take('page:user-settings');
      // Rendered as "Portal Anatomy" in the footer instead — the stock nav
      // item's own disable (`nav-item:app-visualizer: false` in
      // app-config.yaml) doesn't stop it from reaching `navItems.rest()`.
      navItems.take('page:app-visualizer');
      // Rendered as "Actions" in Workspace instead (T1.1) — same caveat as
      // above, `nav-item:scaffolder: false` doesn't stop it reaching `rest()`.
      navItems.take('page:scaffolder');
      // Workspace has its own "Catalog" entry now that `/catalog` is the
      // repository browser (T2.5), so this would be a second copy of it.
      navItems.take('page:catalog');

      // Rendered in the gated "Admin" section instead of "Explore" (T1.4) —
      // platform-team diagnostics, not product surfaces.
      const adminEntries = [
        navItems.take('page:devtools'),
        navItems.take('page:catalog-unprocessed-entities'),
      ]
        .filter((item): item is NavContentNavItem => Boolean(item))
        .map(toEntry);

      const discovered = navItems.rest().map(toEntry);

      return (
        <>
          {/* Keyboard and screen-reader users otherwise have to tab through the
              whole nav on every page (docs/ux-design-review.md §2). The target
              is the routed content region, which Backstage renders after this
              nav in the DOM. */}
          <a
            href="#main-content"
            className="sr-only z-50 rounded-md bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
          >
            Skip to main content
          </a>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Open navigation"
                className="fixed left-3 top-3 z-50 lg:hidden"
              >
                <Menu aria-hidden="true" className="size-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarNav
                discovered={discovered}
                adminEntries={adminEntries}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <nav
            aria-label="Main navigation"
            id={MAIN_NAVIGATION_ID}
            className={cn(
              'fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-border',
              !sidebar.collapsed && 'lg:block',
            )}
          >
            <SidebarNav discovered={discovered} adminEntries={adminEntries} />
          </nav>

          <PortalTopBar
            sidebar={{ ...sidebar, controls: MAIN_NAVIGATION_ID }}
          />
        </>
      );
    },
  },
});
